import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ProfileSetupPage from './pages/ProfileSetupPage';
import ProfilePage from './pages/ProfilePage';
import TheCollectivePage from './pages/TheCollectivePage';
import ForumPage from './pages/ForumPage';
import ForumPostPage from './pages/ForumPostPage';
import NewForumPostPage from './pages/NewForumPostPage';
import AboutPage from './pages/AboutPage';
import FeedbackPage from './pages/FeedbackPage';
import TasksPage from './pages/TasksPage';
import AnalyticsPage from './pages/AnalyticsPage';

// Dashboard components
import Dashboard from './dashboard/Dashboard';
import PresetsPage from './dashboard/components/PresetsPage';
import GrowDashboard from './dashboard/components/GrowDashboard';
import GrowLogPage from './dashboard/components/GrowLogPage';

// Context providers
import { AuthProvider } from './context/AuthContext';
import { DataModeProvider, DATA_MODES } from './context/DataModeContext';
import { GrowProvider } from './dashboard/context/GrowContext';
import { LiveSensorProvider } from './dashboard/context/LiveSensorContext';

// Components
import ProtectedRoute from './components/ProtectedRoute';

// Demo wrapper - provides demo mode context with live updating sensors
function DemoWrapper({ children }) {
  return (
    <DataModeProvider mode={DATA_MODES.DEMO}>
      <LiveSensorProvider updateInterval={3000} historyLength={50}>
        <GrowProvider>
          {children}
        </GrowProvider>
      </LiveSensorProvider>
    </DataModeProvider>
  );
}

// App wrapper - provides live mode context with Firebase
function AppWrapper({ children }) {
  return (
    <DataModeProvider mode={DATA_MODES.LIVE}>
      <GrowProvider>
        {children}
      </GrowProvider>
    </DataModeProvider>
  );
}

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route
            path="/profile-setup"
            element={
              <ProtectedRoute skipProfileCheck>
                <ProfileSetupPage />
              </ProtectedRoute>
            }
          />

          {/* Demo routes - no auth required, static data */}
          <Route
            path="/demo"
            element={
              <DemoWrapper>
                <Dashboard />
              </DemoWrapper>
            }
          />
          <Route
            path="/demo/grow/:growId"
            element={
              <DemoWrapper>
                <GrowDashboard />
              </DemoWrapper>
            }
          />
          <Route
            path="/demo/presets"
            element={
              <DemoWrapper>
                <PresetsPage />
              </DemoWrapper>
            }
          />
          <Route
            path="/demo/grow-log"
            element={
              <DemoWrapper>
                <GrowLogPage />
              </DemoWrapper>
            }
          />
          <Route
            path="/demo/profile"
            element={
              <DemoWrapper>
                <ProfilePage />
              </DemoWrapper>
            }
          />
          <Route
            path="/demo/collective"
            element={
              <DemoWrapper>
                <TheCollectivePage />
              </DemoWrapper>
            }
          />
          <Route
            path="/demo/forum"
            element={
              <DemoWrapper>
                <ForumPage />
              </DemoWrapper>
            }
          />
          <Route
            path="/demo/forum/post/:postId"
            element={
              <DemoWrapper>
                <ForumPostPage />
              </DemoWrapper>
            }
          />
          <Route
            path="/demo/forum/new"
            element={
              <DemoWrapper>
                <NewForumPostPage />
              </DemoWrapper>
            }
          />
          <Route
            path="/demo/analytics"
            element={
              <DemoWrapper>
                <AnalyticsPage />
              </DemoWrapper>
            }
          />
          <Route
            path="/demo/tasks"
            element={
              <DemoWrapper>
                <TasksPage />
              </DemoWrapper>
            }
          />
          <Route
            path="/demo/about"
            element={
              <DemoWrapper>
                <AboutPage />
              </DemoWrapper>
            }
          />
          <Route
            path="/demo/feedback"
            element={
              <DemoWrapper>
                <FeedbackPage />
              </DemoWrapper>
            }
          />

          {/* App routes - auth required, Firebase data */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <Dashboard />
                </AppWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/grow/:growId"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <GrowDashboard />
                </AppWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/presets"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <PresetsPage />
                </AppWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/grow-log"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <GrowLogPage />
                </AppWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/profile"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <ProfilePage />
                </AppWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/collective"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <TheCollectivePage />
                </AppWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/forum"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <ForumPage />
                </AppWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/forum/post/:postId"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <ForumPostPage />
                </AppWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/forum/new"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <NewForumPostPage />
                </AppWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/analytics"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <AnalyticsPage />
                </AppWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/tasks"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <TasksPage />
                </AppWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/about"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <AboutPage />
                </AppWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/feedback"
            element={
              <ProtectedRoute>
                <AppWrapper>
                  <FeedbackPage />
                </AppWrapper>
              </ProtectedRoute>
            }
          />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
